import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, SelectField, TextareaField } from '@/components/ui/field';
import { useClients } from '@/hooks/clients';
import {
  useAddJobsToInvoice,
  useAddLineItem,
  useCreateInvoice,
  useRecordPayment,
  useVoidInvoice,
} from '@/hooks/invoices';
import { useBillableJobs } from '@/hooks/invoices';
import { useServiceTypes } from '@/hooks/catalog-admin';
import { ApiError } from '@/lib/api';
import { formatDay } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { centsToInput, formatCents, parseMoneyInput } from '@shared/money';
import {
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHOD_VALUES,
  type AdminInvoiceDetail,
  type PaymentMethodValue,
} from '@shared/schemas/invoice';

/*
 * The dialogs behind /admin/invoices — ROADMAP.md §12.2 and §12.4.
 *
 * **Money never becomes a float on its way through here** — text in,
 * `parseMoneyInput` to integer cents, integer on the wire; the same discipline
 * as the expense dialogs and for the same IEEE 754 reason.
 *
 * **Nothing in these forms sends a total.** The server recomputes every figure
 * from the lines on every write and ignores anything else — the dialogs only
 * ever send the inputs a person actually typed.
 */

function Shell({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/40" />
        <Dialog.Content
          className={`fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] ${
            wide ? 'w-[min(40rem,calc(100vw-2rem))]' : 'w-[min(32rem,calc(100vw-2rem))]'
          } -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-white p-6 shadow-card`}
        >
          <Dialog.Title className="text-h2 text-ink-900">{title}</Dialog.Title>
          <Dialog.Description className="mt-1 text-body text-ink-500">
            {description}
          </Dialog.Description>
          <div className="mt-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * The job picker both creation dialogs share: this client's completed,
 * not-yet-billed cleans, each showing what its line would say. Checkboxes
 * rather than a multiselect — the office bills a month of cleans at once and
 * needs to see the dates side by side.
 */
function BillableJobPicker({
  clientId,
  selected,
  onToggle,
}: {
  clientId: string | undefined;
  selected: ReadonlySet<string>;
  onToggle: (jobId: string) => void;
}) {
  const billable = useBillableJobs(clientId);

  if (clientId === undefined || clientId === '') {
    return <p className="text-body text-ink-500">Pick a client first.</p>;
  }
  if (billable.isPending) {
    return <p className="text-body text-ink-500">Loading their completed cleans…</p>;
  }
  if (billable.error) {
    return <FormError>Could not load this client’s completed jobs. Try again.</FormError>;
  }

  const jobs = billable.data?.data ?? [];
  if (jobs.length === 0) {
    return (
      <p className="text-body text-ink-500">
        No completed cleans waiting to be billed. A job appears here once it is completed and is not
        already on a live invoice.
      </p>
    );
  }

  return (
    <ul className="max-h-64 space-y-1 overflow-y-auto rounded-sm border border-ink-300 p-2">
      {jobs.map((job) => (
        <li key={job.id}>
          <label className="hover:bg-cream-100 flex min-h-touch cursor-pointer items-center gap-3 rounded-sm px-2 py-1.5">
            <input
              type="checkbox"
              className="size-4 accent-bark-700"
              checked={selected.has(job.id)}
              onChange={() => {
                onToggle(job.id);
              }}
            />
            <span className="flex-1">
              <span className="block text-body text-ink-900">
                {job.serviceName} — {formatDay(job.scheduledDate)}
              </span>
              <span className="block text-meta text-ink-500">
                {job.jobNumber} ·{' '}
                {job.defaultPriceCents === null
                  ? 'No catalog price — set it on the line'
                  : job.pricingUnit === 'hourly'
                    ? `${formatCents(job.defaultPriceCents)}/hr × est. ${(
                        job.estimatedDurationMinutes / 60
                      ).toFixed(1)}h`
                    : formatCents(job.defaultPriceCents)}
              </span>
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// New invoice
// ---------------------------------------------------------------------------

export function NewInvoiceDialog({
  open,
  onOpenChange,
  onCreated,
  presetClientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
  /** From a client detail page, the client is already chosen. */
  presetClientId?: string;
}) {
  const toast = useToast();
  const create = useCreateInvoice();

  const [clientId, setClientId] = useState(presetClientId ?? '');
  const [clientSearch, setClientSearch] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  /*
   * Search-then-pick, the JobCreateDialog pattern, adopted here *before* it
   * failed rather than after: the unsearched list is capped at 100, and that
   * cap has already broken the job dialog's e2e twice (at 25, then at 165
   * clients) when a select was treated as the whole list. The hint below says
   * when the cap is in play.
   */
  const clients = useClients({
    sort: 'name',
    page: 1,
    search: clientSearch,
    activity: 'active',
    highPriorityOnly: false,
    pageSize: 100,
  });

  const apiError = create.error instanceof ApiError ? create.error : null;

  function toggle(jobId: string) {
    const next = new Set(selected);
    if (next.has(jobId)) next.delete(jobId);
    else next.add(jobId);
    setSelected(next);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (clientId === '' || selected.size === 0) return;

    create.mutate(
      { clientId, jobIds: [...selected] },
      {
        onSuccess: (detail) => {
          toast.success(`${detail.invoiceNumber} drafted.`);
          setSelected(new Set());
          onOpenChange(false);
          onCreated(detail.id);
        },
      },
    );
  }

  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="New invoice"
      /*
       * Two sentences, because the preset path has no client to pick — found
       * by opening this dialog from the client detail page, which is the first
       * caller `presetClientId` has ever had (the audit of 2026-08-06).
       */
      description={`${
        presetClientId === undefined
          ? 'Pick the client and the completed cleans to bill.'
          : 'Pick the completed cleans to bill.'
      } Each job becomes a line priced from the rate card, and everything stays editable while it is a draft.`}
      wide
    >
      <form onSubmit={submit} className="space-y-4">
        {presetClientId === undefined ? (
          <>
            {/* Not "Find a client" — the select below is labelled "Client",
                and two labels sharing a word are indistinguishable to a
                screen reader and a test alike (the job dialog's lesson). */}
            <Field
              label="Search by name or phone"
              name="clientSearch"
              type="search"
              value={clientSearch}
              onChange={(event) => {
                setClientSearch(event.target.value);
              }}
              placeholder="Name, business, phone or email"
              hint={
                clients.data !== undefined && clients.data.total > clients.data.data.length
                  ? `Showing ${String(clients.data.data.length)} of ${String(clients.data.total)} — type to narrow it down.`
                  : undefined
              }
            />
            <SelectField
              label="Client"
              value={clientId}
              onChange={(event) => {
                setClientId(event.target.value);
                setSelected(new Set());
              }}
              required
              placeholder="Choose a client…"
              options={(clients.data?.data ?? []).map((row) => ({
                value: row.id,
                label: row.displayName,
              }))}
            />
          </>
        ) : null}

        <div>
          <span className="mb-1 block text-body font-medium text-ink-900">Completed cleans</span>
          <BillableJobPicker clientId={clientId} selected={selected} onToggle={toggle} />
        </div>

        {apiError ? <FormError>{apiError.message}</FormError> : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={create.isPending || clientId === '' || selected.size === 0}
          >
            {create.isPending ? 'Drafting…' : 'Create draft'}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Add more jobs to a draft
// ---------------------------------------------------------------------------

export function AddJobsDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: AdminInvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const addJobs = useAddJobsToInvoice(invoice.id);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const apiError = addJobs.error instanceof ApiError ? addJobs.error : null;

  function toggle(jobId: string) {
    const next = new Set(selected);
    if (next.has(jobId)) next.delete(jobId);
    else next.add(jobId);
    setSelected(next);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (selected.size === 0) return;

    addJobs.mutate([...selected], {
      onSuccess: () => {
        toast.success(selected.size === 1 ? 'Job added.' : `${String(selected.size)} jobs added.`);
        setSelected(new Set());
        onOpenChange(false);
      },
    });
  }

  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Add completed cleans"
      description={`More of ${invoice.clientName}’s completed cleans onto ${invoice.invoiceNumber}.`}
      wide
    >
      <form onSubmit={submit} className="space-y-4">
        <BillableJobPicker clientId={invoice.clientId} selected={selected} onToggle={toggle} />
        {apiError ? <FormError>{apiError.message}</FormError> : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={addJobs.isPending || selected.size === 0}>
            {addJobs.isPending ? 'Adding…' : 'Add to invoice'}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Add a line — from the rate card, or typed
// ---------------------------------------------------------------------------

export function AddLineDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: AdminInvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const addLine = useAddLineItem(invoice.id);

  const [mode, setMode] = useState<'catalog' | 'custom'>('catalog');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [description, setDescription] = useState('');
  const [pricingUnit, setPricingUnit] = useState<'flat' | 'hourly'>('flat');
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const services = useServiceTypes({ page: 1, search: '', activity: 'active', pageSize: 100 });
  // The invoice builder offers what is billable — is_requestable is the
  // request form's list, and the two genuinely differ (business-rules.md §7).
  const billable = (services.data?.data ?? []).filter((row) => row.isBillable);

  const apiError = addLine.error instanceof ApiError ? addLine.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    setFieldError(undefined);

    if (mode === 'catalog') {
      if (serviceTypeId === '') {
        setFieldError('Pick a service');
        return;
      }
      const parsedQuantity = Number(quantity);
      if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
        setFieldError('Quantity must be more than zero');
        return;
      }
      addLine.mutate(
        { kind: 'catalog', serviceTypeId, quantity: parsedQuantity },
        {
          onSuccess: () => {
            toast.success('Line added from the rate card.');
            onOpenChange(false);
          },
        },
      );
      return;
    }

    const parsedPrice = parseMoneyInput(price);
    if (!parsedPrice.ok) {
      setFieldError(parsedPrice.message);
      return;
    }
    if (parsedPrice.cents === null) {
      setFieldError('Enter a price — 0 is fine for “no charge”');
      return;
    }
    const parsedQuantity = Number(quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setFieldError('Quantity must be more than zero');
      return;
    }
    if (description.trim() === '') {
      setFieldError('Every line needs a description');
      return;
    }

    addLine.mutate(
      {
        kind: 'custom',
        description: description.trim(),
        pricingUnit,
        quantity: parsedQuantity,
        unitPriceCents: parsedPrice.cents,
      },
      {
        onSuccess: () => {
          toast.success('Line added.');
          setDescription('');
          setPrice('');
          onOpenChange(false);
        },
      },
    );
  }

  const selectedService = billable.find((row) => row.id === serviceTypeId);

  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Add a line"
      description="Pick a service and the description, unit and price fill in from the rate card — then stay editable. Or type a one-off charge that is in no catalog."
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="flex gap-2" role="group" aria-label="Line kind">
          <Button
            type="button"
            variant={mode === 'catalog' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => {
              setMode('catalog');
            }}
          >
            From the rate card
          </Button>
          <Button
            type="button"
            variant={mode === 'custom' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => {
              setMode('custom');
            }}
          >
            One-off charge
          </Button>
        </div>

        {mode === 'catalog' ? (
          <>
            <SelectField
              label="Service"
              value={serviceTypeId}
              onChange={(event) => {
                setServiceTypeId(event.target.value);
              }}
              required
              placeholder="Choose a service…"
              options={billable.map((row) => ({
                value: row.id,
                label: `${row.name}${
                  row.defaultPriceCents === null
                    ? ' — no price set'
                    : ` — ${formatCents(row.defaultPriceCents)}${row.pricingUnit === 'hourly' ? '/hr' : ''}`
                }`,
              }))}
            />
            <Field
              label={selectedService?.pricingUnit === 'hourly' ? 'Hours' : 'Quantity'}
              type="number"
              // min and step travel together: flat counts whole units from 1,
              // hourly counts quarter-hours from 0.25. A mismatched pair puts
              // 1 off the step grid and the browser refuses the default —
              // the e2e run caught exactly that on a screenshot.
              step={selectedService?.pricingUnit === 'hourly' ? '0.25' : '1'}
              min={selectedService?.pricingUnit === 'hourly' ? '0.25' : '1'}
              value={quantity}
              onChange={(event) => {
                setQuantity(event.target.value);
              }}
              hint={
                selectedService?.pricingUnit === 'hourly'
                  ? 'The line total is rate × hours — 2.5 is two and a half hours.'
                  : undefined
              }
            />
          </>
        ) : (
          <>
            <Field
              label="Description"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
              required
              placeholder="Key cutting for the lockbox"
            />
            {/* Not "Priced": the field below is "Price", and two labels
                sharing a word are indistinguishable to a screen reader and a
                strict locator alike — the same lesson as the client search. */}
            <SelectField
              label="How it is charged"
              value={pricingUnit}
              onChange={(event) => {
                setPricingUnit(event.target.value as 'flat' | 'hourly');
                setQuantity('1');
              }}
              options={[
                { value: 'flat', label: 'Flat — quantity × price' },
                { value: 'hourly', label: 'Hourly — rate × hours' },
              ]}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={pricingUnit === 'hourly' ? 'Hours' : 'Quantity'}
                type="number"
                step={pricingUnit === 'hourly' ? '0.25' : '1'}
                min={pricingUnit === 'hourly' ? '0.25' : '1'}
                value={quantity}
                onChange={(event) => {
                  setQuantity(event.target.value);
                }}
              />
              <Field
                label={pricingUnit === 'hourly' ? 'Rate per hour' : 'Price'}
                inputMode="decimal"
                value={price}
                onChange={(event) => {
                  setPrice(event.target.value);
                }}
                placeholder="125.00"
                required
              />
            </div>
          </>
        )}

        {fieldError !== undefined ? <FormError>{fieldError}</FormError> : null}
        {apiError ? <FormError>{apiError.message}</FormError> : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={addLine.isPending}>
            {addLine.isPending ? 'Adding…' : 'Add line'}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Recording a payment — §12.4
// ---------------------------------------------------------------------------

export function RecordPaymentDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: AdminInvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const record = useRecordPayment(invoice.id);

  const [amount, setAmount] = useState(() =>
    invoice.balanceCents > 0 ? centsToInput(invoice.balanceCents) : '',
  );
  const [method, setMethod] = useState<PaymentMethodValue>('check');
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [amountError, setAmountError] = useState<string | undefined>(undefined);

  const apiError = record.error instanceof ApiError ? record.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();

    const parsed = parseMoneyInput(amount);
    if (!parsed.ok) {
      setAmountError(parsed.message);
      return;
    }
    if (parsed.cents === null || parsed.cents === 0) {
      setAmountError('Enter the amount that arrived');
      return;
    }
    setAmountError(undefined);

    record.mutate(
      {
        amountCents: parsed.cents,
        method,
        paidOn,
        reference: reference.trim() === '' ? null : reference.trim(),
        notes: notes.trim() === '' ? null : notes.trim(),
      },
      {
        onSuccess: (detail) => {
          toast.success(
            detail.status === 'paid'
              ? `${formatCents(parsed.cents ?? 0)} recorded — ${detail.invoiceNumber} is paid.`
              : `${formatCents(parsed.cents ?? 0)} recorded — ${formatCents(detail.balanceCents)} still owed.`,
          );
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Record a payment"
      description={`Money that has already arrived against ${invoice.invoiceNumber}. Partial payments are fine — the invoice is paid when the balance is met.`}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Amount"
          inputMode="decimal"
          value={amount}
          onChange={(event) => {
            setAmount(event.target.value);
          }}
          error={amountError}
          required
          hint={`Balance: ${formatCents(invoice.balanceCents)}`}
        />
        <SelectField
          label="How it arrived"
          value={method}
          onChange={(event) => {
            setMethod(event.target.value as PaymentMethodValue);
          }}
          options={PAYMENT_METHOD_VALUES.map((value) => ({
            value,
            label: PAYMENT_METHOD_LABELS[value],
          }))}
        />
        <Field
          label="Date the money arrived"
          type="date"
          value={paidOn}
          onChange={(event) => {
            setPaidOn(event.target.value);
          }}
          required
          // §12.4's whole point, said where the person is looking.
          hint="The day it was received — a cheque from Tuesday’s mail entered today is Tuesday’s payment."
        />
        <Field
          label="Reference"
          value={reference}
          onChange={(event) => {
            setReference(event.target.value);
          }}
          placeholder="Cheque number, last four, confirmation code"
        />
        <TextareaField
          label="Notes"
          value={notes}
          onChange={(event) => {
            setNotes(event.target.value);
          }}
          rows={2}
        />

        {apiError ? <FormError>{apiError.message}</FormError> : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={record.isPending}>
            {record.isPending ? 'Recording…' : 'Record payment'}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Voiding — §12.2's "void, never delete"
// ---------------------------------------------------------------------------

export function VoidInvoiceDialog({
  invoice,
  open,
  onOpenChange,
}: {
  invoice: AdminInvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const voidInvoice = useVoidInvoice(invoice.id);
  const [reason, setReason] = useState('');

  const apiError = voidInvoice.error instanceof ApiError ? voidInvoice.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (reason.trim() === '') return;

    voidInvoice.mutate(reason.trim(), {
      onSuccess: () => {
        toast.success(`${invoice.invoiceNumber} voided. Its number stays burnt.`);
        onOpenChange(false);
      },
    });
  }

  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title={`Void ${invoice.invoiceNumber}`}
      description="An invoice is never deleted — voiding keeps the record and frees its jobs to be billed again. To change what it says, void it and reissue."
    >
      <form onSubmit={submit} className="space-y-4">
        <TextareaField
          label="Why"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
          rows={2}
          required
          hint="Kept on the record — a void with no reason is not a financial record."
        />

        {apiError ? <FormError>{apiError.message}</FormError> : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Keep it
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={voidInvoice.isPending || reason.trim() === ''}
          >
            {voidInvoice.isPending ? 'Voiding…' : 'Void invoice'}
          </Button>
        </div>
      </form>
    </Shell>
  );
}
